'use client'

import { useRouter } from 'next/navigation'
import {
  ArrowRight,
  GitBranch,
  Code2,
  ShieldCheck,
  Video,
  Wrench,
  RefreshCw,
  Users,
  BookOpen,
  Calendar,
  Shield,
} from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
import { useButtonTracking } from '@/lib/useButtonTracking'
import Navbar from '@/components/home/Navbar'
import Pricing from '@/components/home/Pricing'
import Learning from '@/components/home/Learning'
import Footer from '@/components/home/Footer'

const REPO_URL = 'https://github.com/JayZeeDesign/loop-engineer-template'
const VIDEO_ID = 'W6x-hb44C0c'
const SECTION = 'lp_loop_engineer'

const trustStats = [
  { icon: Users, value: '1,000+', label: 'Builders' },
  { icon: BookOpen, value: '15+', label: 'Courses' },
  { icon: Video, value: '25+', label: 'Expert Talks' },
  { icon: Calendar, value: 'Weekly', label: 'Live Workshops' },
  { icon: Shield, value: '30-Day', label: 'Money-Back Guarantee' },
]

const workshopPoints = [
  { icon: Video, text: '1-hour live session, every week' },
  { icon: Wrench, text: 'Set up the codebase harness so agents verify their own work' },
  { icon: RefreshCw, text: 'Build self-improving loops that compound' },
]

export default function LoopEngineerLanding() {
  const isMobile = useIsMobile()
  const router = useRouter()
  const { trackButtonClick } = useButtonTracking()

  const goSignup = (button_name: string) => {
    trackButtonClick('lp_cta_click', {
      section: SECTION,
      button_name,
      destination: '/auth/signup',
      conversion: true,
    })
    router.push('/auth/signup')
  }

  const goRepo = (button_name: string) => {
    trackButtonClick('lp_repo_click', {
      section: SECTION,
      button_name,
      destination: REPO_URL,
    })
    window.open(REPO_URL, '_blank')
  }

  return (
    <main className="bg-background text-white">
      <Navbar isMobile={isMobile} isHomePage={false} />

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-white/70">
            <GitBranch className="h-4 w-4" />
            You just grabbed loop-engineer-template
          </div>
          <h1 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Launch your first <span className="text-primary">AI loops</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/70 md:text-xl">
            Loops are agents that trigger themselves, do the work, verify it, and learn, so it
            compounds without you prompting every step. Grab the free template, then learn to run
            them for real inside AI Builder Club.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              onClick={() => goSignup('hero_primary')}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-black transition hover:opacity-90"
            >
              Join AI Builder Club
              <ArrowRight className="h-5 w-5 transition group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => goRepo('hero_repo')}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-white transition hover:bg-white/5"
            >
              <Code2 className="h-5 w-5" />
              View the repo
            </button>
          </div>
        </div>
      </section>

      {/* Trust bar (stats from /courses) */}
      <section className="px-4 pb-10 md:pb-16">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-xl border border-gray-800 bg-[#111111] px-4 py-4 md:rounded-2xl md:px-6 md:py-8">
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 md:flex-nowrap md:justify-around md:gap-0">
              {trustStats.map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-2 md:flex-col md:items-center md:text-center"
                >
                  <stat.icon className="h-4 w-4 text-gray-400 md:mb-2 md:h-6 md:w-6" />
                  <span className="text-sm font-bold text-white md:text-lg">{stat.value}</span>
                  <span className="text-xs text-gray-400 md:text-sm">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Weekly live workshop */}
      <section className="px-6 py-16 md:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
              Weekly live workshop
            </div>
            <h2 className="text-3xl font-bold md:text-4xl">Build it live with us, every week</h2>
            <p className="mt-4 text-white/65">
              Each week we go deep on a real build, live. Recent 1-hour walkthroughs covered
              setting up the codebase harness and wiring self-improving loops, the exact setup
              running in my company. Miss one? Every session lands in the library.
            </p>
            <ul className="mt-7 space-y-4">
              {workshopPoints.map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <span className="pt-1.5 text-white/85">{p.text}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => goSignup('workshop_primary')}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-black transition hover:opacity-90"
            >
              Join the next workshop
              <ArrowRight className="h-5 w-5 transition group-hover:translate-x-0.5" />
            </button>
          </div>
          <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube.com/embed/${VIDEO_ID}`}
              title="The loop engineer: how to design compounding agent loops"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Courses (home page section) */}
      <Learning isMobile={isMobile} />

      {/* Reuse the real pricing block (its testimonial carousel sits below the plans) */}
      <Pricing
        isMobile={isMobile}
        headingOverride="Run your company in AI native way in 30 days"
        popularBadgeOverride="1:1 Mentorship Sessions"
        extraFeatures={[{ text: 'Exclusive perks' }]}
      />

      {/* Final CTA */}
      <section className="px-6 py-20 text-center">
        <div className="mx-auto max-w-2xl">
          <ShieldCheck className="mx-auto mb-5 h-10 w-10 text-primary" />
          <h2 className="text-3xl font-bold md:text-4xl">Come build with us</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-white/65">
            The repo gets you the skeleton. The club gets you running loops for real, with people
            doing the same.
          </p>
          <button
            onClick={() => goSignup('footer_primary')}
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-black transition hover:opacity-90"
          >
            Join AI Builder Club
            <ArrowRight className="h-5 w-5 transition group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>

      <Footer isMobile={isMobile} />
    </main>
  )
}
